// AuthContext.tsx - 修复重复调用问题

import { createContext, useContext, useEffect, useState, useRef } from 'react'
import type { ReactNode } from 'react'
import {
  smartLogin,
  validateThirdPartyLogin,
  validateLogin,
  getUserInfo,
  getAuthStatus,
  clearAuth,
  type ThirdPartyLoginCmd,
  type LoginCmd,
  type LoginResponse
} from '../services/auth'
import Loading from '../components/Loading'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: any
  error: string | null
}

interface AuthContextType extends AuthState {
  loginWithThirdParty: (params: ThirdPartyLoginCmd) => Promise<LoginResponse>
  loginWithCredentials: (params: LoginCmd) => Promise<LoginResponse>
  smartLogin: () => Promise<LoginResponse>
  logout: () => void
  refetch: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null
  })

  // 🔧 添加防重复调用的ref
  const isInitializing = useRef(false)
  const hasInitialized = useRef(false)

  // 初始化認證
  const initAuth = async () => {
    // 🔧 防止重复调用
    if (isInitializing.current || hasInitialized.current) {
      console.log('🛡️ 防止重复初始化认证')
      return
    }

    isInitializing.current = true

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }))

      // 1. 检查当前认证状态
      const authStatus = getAuthStatus()

      if (authStatus.isAuthenticated) {
        // 已经有有效的认证状态
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          user: authStatus.userInfo,
          isLoading: false,
          error: null
        }))
        hasInitialized.current = true
        return
      }

      // 2. 没有有效认证，尝试智能登录
      try {
        const loginResult = await smartLogin()
        console.log("loginResult",loginResult)
        // 登录成功，获取用户信息
        const userInfo = getUserInfo()

        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          user: userInfo,
          isLoading: false,
          error: null
        }))
        hasInitialized.current = true

      } catch (loginError) {
        console.warn('智能登录失败:', loginError)

        // 在开发环境下，如果智能登录失败，可以使用模拟数据
        if (import.meta.env.DEV) {
          try {
            console.log('开发环境：尝试使用模拟认证数据')
            const mockParams: ThirdPartyLoginCmd = {
              thirdPartyUserId: 'mock_user_001',
              nonce: 'mock_nonce_' + Date.now(),
              timestamp: Date.now(),
              signature: 'mock_signature_' + Math.random()
            }

            await validateThirdPartyLogin(mockParams)
            const userInfo = getUserInfo()

            setState(prev => ({
              ...prev,
              isAuthenticated: true,
              user: userInfo,
              isLoading: false,
              error: null
            }))

            console.log('模拟登录成功:', userInfo)
            hasInitialized.current = true

          } catch (mockError) {
            console.error('模拟登录也失败:', mockError)
            setState(prev => ({
              ...prev,
              isAuthenticated: false,
              user: null,
              isLoading: false,
              error: mockError instanceof Error ? mockError.message : '模拟登录失败'
            }))
          }
        } else {
          // 生产环境下，登录失败就是失败
          setState(prev => ({
            ...prev,
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: loginError instanceof Error ? loginError.message : '登录失败'
          }))
        }
      }

    } catch (error) {
      console.error('认证初始化失败:', error)
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: error instanceof Error ? error.message : '认证初始化失败'
      }))
    } finally {
      isInitializing.current = false
    }
  }

  // 第三方登录函数
  const loginWithThirdParty = async (params: ThirdPartyLoginCmd): Promise<LoginResponse> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result = await validateThirdPartyLogin(params)

      // 登录成功，获取用户信息
      const userInfo = getUserInfo()

      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: userInfo,
        isLoading: false,
        error: null
      }))

      return result
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '第三方登录失败'
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: errorMsg
      }))
      throw error
    }
  }

  // 普通登录函数
  const loginWithCredentials = async (params: LoginCmd): Promise<LoginResponse> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result = await validateLogin(params)

      // 登录成功，获取用户信息
      const userInfo = getUserInfo()

      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: userInfo,
        isLoading: false,
        error: null
      }))

      return result
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '登录失败'
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: errorMsg
      }))
      throw error
    }
  }

  // 智能登录函数（封装）
  const handleSmartLogin = async (): Promise<LoginResponse> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const result = await smartLogin()

      // 登录成功，获取用户信息
      const userInfo = getUserInfo()

      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: userInfo,
        isLoading: false,
        error: null
      }))

      return result
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : '智能登录失败'
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: errorMsg
      }))
      throw error
    }
  }

  // 登出函数
  const logout = () => {
    clearAuth()
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null
    })
    // 🔧 重置初始化状态
    hasInitialized.current = false
    isInitializing.current = false
  }

  // 重新获取用户信息
  const refetch = async () => {
    // 🔧 重置状态后重新初始化
    hasInitialized.current = false
    isInitializing.current = false
    await initAuth()
  }

  // 🔧 修改useEffect，添加依赖检查
  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      if (isMounted && !hasInitialized.current) {
        await initAuth()
      }
    }

    initialize()

    // 清理函数
    return () => {
      isMounted = false
    }
  }, []) // 空依赖数组，只在挂载时执行一次

  const contextValue: AuthContextType = {
    ...state,
    loginWithThirdParty,
    loginWithCredentials,
    smartLogin: handleSmartLogin,
    logout,
    refetch
  }

  // 🔧 修改Loading组件，移除重复的认证逻辑
  if (state.isLoading) {
    return (
        <Loading
            message={window.parent !== window ? '正在驗證身份' : '正在載入遊戲'}
            showProgress={true}
            minDuration={3500}
            stepDelays={{
              checkAuth: 600,
              login: 1000,
              userData: 800,
              gameData: 800
            }}
            onLoadingComplete={(result) => {
              // 🔧 Loading完成后不再执行额外的认证逻辑
              // 只是更新最终状态
              if (result.success) {
                setState(prev => ({
                  ...prev,
                  isAuthenticated: true,
                  user: result.userInfo || prev.user,
                  isLoading: false,
                  error: null
                }))
              } else {
                setState(prev => ({
                  ...prev,
                  isAuthenticated: false,
                  user: null,
                  isLoading: false,
                  error: result.error || '載入失敗'
                }))
              }
            }}
        />
    )
  }

  // 如果认证失败，显示错误页面
  if (!state.isAuthenticated && state.error) {
    return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'var(--gradient-primary)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '4em', marginBottom: '20px' }}>❌</div>
          <h2 style={{ fontSize: '2em', marginBottom: '20px', fontWeight: '600' }}>認證失敗</h2>
          <p style={{
            fontSize: '1.2em',
            marginBottom: '30px',
            maxWidth: '500px',
            lineHeight: '1.5',
            opacity: 0.9
          }}>
            {state.error}
          </p>
          <button
              onClick={refetch}
              style={{
                background: 'linear-gradient(45deg, #ff6b6b, #ff8787)',
                border: 'none',
                borderRadius: '25px',
                padding: '15px 30px',
                fontSize: '1.1em',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
                transition: 'all 0.3s ease'
              }}
          >
            重新載入
          </button>

          {/* 开发环境调试信息 */}
          {import.meta.env.DEV && (
              <details style={{
                marginTop: '30px',
                maxWidth: '600px',
                background: 'rgba(0, 0, 0, 0.2)',
                padding: '15px',
                borderRadius: '10px',
                textAlign: 'left'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginBottom: '10px',
                  textAlign: 'center'
                }}>
                  調試信息
                </summary>
                <pre style={{
                  margin: 0,
                  fontSize: '0.8em',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  opacity: 0.8
                }}>
              {JSON.stringify({
                error: state.error,
                url: window.location.href,
                isIframe: window.parent !== window,
                hasInitialized: hasInitialized.current,
                isInitializing: isInitializing.current
              }, null, 2)}
            </pre>
              </details>
          )}
        </div>
    )
  }

  return (
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}